import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { assetToast } from '@xdocs/i18n';
import { searchQuery } from '@xdocs/schemas';
import type { DifficultyLevel, DocumentKind, ErrorAlert, SearchQueryResult, SearchResultRow, Suppression } from '@xdocs/schemas';
import { useDebouncedValue, useI18n, useToast } from '@xdocs/ui';
import { useAuth } from '../../auth/AuthProvider.js';
import { useWs } from '../../ws/WsProvider.js';
import { useSearchT } from './useSearchT.js';

/** Below this many characters of TYPED query text, `search.query` never
 * fires — avoids a noisy request per keystroke on 1-character input while
 * still allowing a pure-filter browse (empty `q`, F31 category chips only). */
export const MIN_QUERY_CHARS = 2;
const DEBOUNCE_MS = 250;
const PAGE_SIZE = 20;

export type SearchSort = 'relevance' | 'updatedAt';
export type SearchDir = 'asc' | 'desc';

export interface SearchFiltersState {
  kind: DocumentKind | null;
  categoryId: string | null;
  language: string;
  difficultyLevel: DifficultyLevel | null;
}

export const DEFAULT_SEARCH_FILTERS: SearchFiltersState = {
  kind: null,
  categoryId: null,
  language: '',
  difficultyLevel: null,
};

export interface UseSearchQueryResult {
  qDraft: string;
  setQDraft: (value: string) => void;
  isBelowMinChars: boolean;
  hasSearched: boolean;
  filters: SearchFiltersState;
  setFilters: (next: SearchFiltersState) => void;
  sort: SearchSort;
  dir: SearchDir;
  setSort: (sort: SearchSort, dir: SearchDir) => void;
  rows: readonly SearchResultRow[];
  total: number | null;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
  onLoadMore: () => void;
  suppress: (result: SearchResultRow) => void;
  hidingShareId: string | null;
}

/**
 * `search.query` (F8) with a debounced + min-chars-guarded text input, F31
 * filters/sort, mobile-rule "Load more" append pagination, and F30
 * per-result suppression with an optimistic remove + rollback-on-failure +
 * "Hidden — Undo" toast (ux.md: low-stakes/reversible → Undo toast, no
 * confirm dialog).
 */
export function useSearchQuery(): UseSearchQueryResult {
  const { userId } = useAuth();
  const { wsClient, connectionState } = useWs();
  const { locale, t: tGlobal } = useI18n();
  const { t } = useSearchT();
  const toast = useToast();

  const [qDraft, setQDraft] = useState('');
  const [filters, setFiltersState] = useState<SearchFiltersState>(DEFAULT_SEARCH_FILTERS);
  const [sort, setSortState] = useState<SearchSort>('relevance');
  const [dir, setDirState] = useState<SearchDir>('desc');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<SearchResultRow[]>([]);
  const [hidingShareId, setHidingShareId] = useState<string | null>(null);

  const appendModeRef = useRef(false);

  const debouncedQ = useDebouncedValue(qDraft, DEBOUNCE_MS);
  const trimmedQ = debouncedQ.trim();
  const isBelowMinChars = trimmedQ.length > 0 && trimmedQ.length < MIN_QUERY_CHARS;
  const effectiveQ = trimmedQ.length >= MIN_QUERY_CHARS ? trimmedQ : '';

  // The free-text language filter gets the SAME debounce treatment as the
  // main query box (F8's own documented "debounced input" edge case, E6's
  // ~250ms guidance) — `filters.language` still updates on every keystroke
  // for the controlled `<Input>` to feel responsive, but only the debounced
  // value feeds the WS request / page-reset criteria below.
  const debouncedLanguage = useDebouncedValue(filters.language, DEBOUNCE_MS);
  const effectiveLanguage = debouncedLanguage.trim();

  const hasSearched = effectiveQ.length > 0 || filters.kind !== null || filters.categoryId !== null || filters.difficultyLevel !== null || effectiveLanguage.length > 0;

  const queryInput = {
    page,
    pageSize: PAGE_SIZE,
    sort,
    dir,
    ...(effectiveQ ? { q: effectiveQ } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(filters.categoryId ? { categoryId: filters.categoryId } : {}),
    ...(effectiveLanguage ? { language: effectiveLanguage } : {}),
    ...(filters.difficultyLevel ? { difficultyLevel: filters.difficultyLevel } : {}),
  };

  // Reset to page 1 (replace mode) whenever the effective search/filter/sort
  // criteria change — mirrors `useDocumentsList`'s `onQueryChange` reset,
  // adapted for this hook owning debounce internally instead of `<DataTable>`.
  // Uses the DEBOUNCED language, not raw `filters`, so a keystroke in the
  // language box doesn't reset the page / fire a request on every character.
  const criteriaKey = JSON.stringify({
    effectiveQ,
    kind: filters.kind,
    categoryId: filters.categoryId,
    difficultyLevel: filters.difficultyLevel,
    language: effectiveLanguage,
    sort,
    dir,
  });
  const prevCriteriaKey = useRef(criteriaKey);
  useEffect(() => {
    if (prevCriteriaKey.current === criteriaKey) return;
    prevCriteriaKey.current = criteriaKey;
    appendModeRef.current = false;
    setPage(1);
  }, [criteriaKey]);

  // Never fires with nothing to search on — a bare mount with no text and no
  // filters stays in the first-use empty state rather than issuing a
  // "browse everything" request no UI here has asked for.
  const enabled = connectionState === 'connected' && userId !== null && !isBelowMinChars && hasSearched;

  const result = useQuery<SearchQueryResult, ErrorAlert>({
    queryKey: ['search', userId, 'query', queryInput],
    queryFn: () => requestTyped(wsClient, 'search.query', searchQuery.parse(queryInput)),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  // Below the min-chars guard there is no live query to source rows from —
  // clear stale rows rather than leaving the previous search's results on
  // screen mid-edit.
  useEffect(() => {
    if (isBelowMinChars) setRows([]);
  }, [isBelowMinChars]);

  const setFilters = useCallback((next: SearchFiltersState) => setFiltersState(next), []);
  const setSort = useCallback((nextSort: SearchSort, nextDir: SearchDir) => {
    setSortState(nextSort);
    setDirState(nextDir);
  }, []);

  const onLoadMore = useCallback(() => {
    if (result.isFetching) return;
    appendModeRef.current = true;
    setPage((p) => p + 1);
  }, [result.isFetching]);

  const onRetry = useCallback(() => void result.refetch(), [result]);

  const suppressMutation = useMutation<
    Suppression,
    ErrorAlert,
    SearchResultRow,
    { index: number; row: SearchResultRow }
  >({
    mutationFn: (row) => requestTyped(wsClient, 'suppression.add', { shareId: row.shareId }),
    onMutate: (row) => {
      const index = rows.findIndex((candidate) => candidate.shareId === row.shareId);
      setHidingShareId(row.shareId);
      setRows((prev) => prev.filter((candidate) => candidate.shareId !== row.shareId));
      return { index, row };
    },
    onError: (_err, _row, context) => {
      if (context) {
        setRows((prev) => {
          const next = [...prev];
          next.splice(Math.min(context.index, next.length), 0, context.row);
          return next;
        });
      }
      toast.push({ tone: 'error', message: t('search:toast.hideFailed') });
    },
    onSuccess: (_result, row, context) => {
      const { message } = assetToast(locale, 'success', t('search:toast.hidden.action'), row.title);
      toast.push({
        tone: 'success',
        message,
        undoLabel: tGlobal('common:action.undo'),
        onUndo: () => {
          void requestTyped(wsClient, 'suppression.remove', { shareId: row.shareId })
            .then(() => {
              setRows((prev) => {
                if (prev.some((candidate) => candidate.shareId === row.shareId)) return prev;
                const next = [...prev];
                next.splice(Math.min(context?.index ?? 0, next.length), 0, row);
                return next;
              });
            })
            .catch(() => {
              toast.push({ tone: 'error', message: t('search:toast.undoFailed') });
            });
        },
      });
    },
    // Deliberately no eager `invalidateQueries` here: the real backend's
    // `search.query` already anti-joins this user's `suppressions` (so a
    // forced refetch would be redundant once the server round-trip lands),
    // and forcing one immediately would race the optimistic removal above —
    // the mutation response hasn't taught the CLIENT's mock/cache anything
    // new the local optimistic state doesn't already reflect. Reconciliation
    // on the freshest server state happens the normal way: the `search:
    // {userId}` WS push (E1 §4 "suppression added → invalidateTag(...)",
    // §5.5 `search-index` channel) already flows through the generic
    // `ws/queryInvalidation.ts` push→invalidate skeleton this hook's
    // `['search', userId, …]` query key is deliberately prefixed to match.
    onSettled: () => {
      setHidingShareId(null);
    },
  });

  const suppress = useCallback((row: SearchResultRow) => suppressMutation.mutate(row), [suppressMutation]);

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    qDraft,
    setQDraft,
    isBelowMinChars,
    hasSearched,
    filters,
    setFilters,
    sort,
    dir,
    setSort,
    rows,
    total,
    hasMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry,
    onLoadMore,
    suppress,
    hidingShareId,
  };
}
